import { Rating } from "@shared/ui.components";
import { colours } from "@shared/ui.design-system";
import { Typography } from "@shared/ui.primitives";
import Image from "next/image";

import "@shared/ui.primitives/style.css";

import { card, productInfo } from "./FreeGiftOfferCard.css";

export interface FreeGiftOfferCardProps {
  imageUrl: string;
  imageAlt: string;
  franchiseName: string;
  productName: string;
  rating: number;
  ratingCount: number;
}

/**
 * Card component displaying a free gift offer with product image, rating, and details
 *
 * @param {Object} props - Props for the FreeGiftOfferCard component
 * @param {string} props.imageUrl - URL for the product image
 * @param {string} props.imageAlt - Alt text for the product image
 * @param {string} props.franchiseName - Name of the franchise/brand
 * @param {string} props.productName - Name of the product
 * @param {number} props.rating - Product rating value (0-5)
 * @param {number} props.ratingCount - Number of reviews for the rating
 * @return {JSX.Element} Rendered FreeGiftOfferCard component
 */
export const FreeGiftOfferCard: React.FC<FreeGiftOfferCardProps> = ({
  imageUrl,
  imageAlt,
  franchiseName,
  productName,
  rating,
  ratingCount
}) => {
  return (
    <article
      className={card}
      data-testid="free-gift-offer-card"
      aria-label={`Free gift offer: ${franchiseName} - ${productName}`}
    >
      <Image src={imageUrl} alt={imageAlt} width={90} height={90} />

      <div className={productInfo}>
        <Rating
          ratingValue={rating}
          totalReviews={ratingCount}
          renderCountAsLink={false}
          includeCountSuffix={false}
          activeColor={colours.primary.dark}
          inactiveColor={colours.secondary.dark}
        />

        <div>
          <Typography as="h4" variant="franchise-sm" colour="primary-dark">
            {franchiseName}
          </Typography>

          <Typography variant="product-sm" colour="primary-dark">
            {productName}
          </Typography>
        </div>
      </div>
    </article>
  );
};
